import { NavLink, useNavigate } from "react-router-dom";

import { PanelOutlet } from "../../components/PanelOutlet";
import { BuildingIcon, Button, ChartIcon, LogOutIcon, Logo, SettingsIcon } from "../../components/ui";
import { useAuth } from "../../lib/auth";
import { useMobile } from "../../lib/useMediaQuery";

const NAV = [
  { to: "/", label: "Lokale", end: true, Icon: BuildingIcon },
  { to: "/stats", label: "Statystyki", end: false, Icon: ChartIcon },
  { to: "/settings", label: "Ustawienia", end: false, Icon: SettingsIcon },
];

/**
 * Operator shell - the venue shell minus the scanner: 232px sidebar with the
 * operator card pinned at the bottom, or a sticky header and tab strip under
 * 900px. Venue detail pages match "/" for the Lokale tab.
 */
export function OperatorLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const mobile = useMobile();
  const email = user?.email ?? "";
  const initials = (user?.name || email).slice(0, 2).toUpperCase();

  const isActive = (to: string, end: boolean, active: boolean) => active || (end && to === "/" && window.location.pathname.startsWith("/venues"));

  if (mobile) {
    return (
      <div className="flex min-h-full flex-col">
        <header className="sticky top-0 z-20 border-b border-line-soft bg-[rgba(8,8,12,0.82)] backdrop-blur-[14px]">
          <div className="flex h-14 items-center justify-between px-4">
            <div className="flex items-center gap-2.5">
              <Logo size={18} />
              <span className="h-4 w-px bg-line-strong" />
              <span className="text-[13px] font-semibold">Operator</span>
            </div>
            <Button variant="ghost" size="sm" className="text-muted" onClick={() => void logout()}>
              Wyloguj
            </Button>
          </div>
          <nav className="grid grid-cols-3 px-2">
            {NAV.map((tab) => (
              <NavLink key={tab.to} to={tab.to} end={tab.end} className="relative flex h-11 items-center justify-center text-[13px] font-medium">
                {({ isActive: active }) => {
                  const on = isActive(tab.to, tab.end, active);
                  return (
                    <span className={on ? "text-ink" : "text-muted"}>
                      {tab.label}
                      {on && <span className="absolute -bottom-px left-2.5 right-2.5 h-0.5 rounded-sm bg-accent-2" />}
                    </span>
                  );
                }}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="min-w-0 flex-1 px-4 pb-12 pt-5">
          <PanelOutlet />
        </main>
      </div>
    );
  }

  return (
    <div className="grid min-h-full grid-cols-[232px_minmax(0,1fr)]">
      <aside className="sticky top-0 flex h-screen flex-col gap-1 border-r border-line-soft px-3.5 py-5">
        <div className="flex items-center gap-2.5 px-2.5 pb-[22px] pt-1">
          <Logo size={20} />
          <span className="rounded-full border border-line-strong px-[7px] py-0.5 text-[11px] font-medium text-muted">operator</span>
        </div>

        {NAV.map((tab) => (
          <NavLink key={tab.to} to={tab.to} end={tab.end} className="flex h-9 items-center gap-2.5 rounded-sm px-2.5 text-[13px] font-medium transition duration-150">
            {({ isActive: active }) => {
              const on = isActive(tab.to, tab.end, active);
              return (
                <span className={`flex h-full w-full items-center gap-2.5 rounded-sm px-2.5 -mx-2.5 ${on ? "bg-glass-2 text-ink" : "text-muted hover:text-ink"}`}>
                  <span className={on ? "text-accent-2" : "text-faint"}>
                    <tab.Icon size={16} />
                  </span>
                  {tab.label}
                </span>
              );
            }}
          </NavLink>
        ))}

        <div className="mt-auto grid gap-2.5 rounded-[12px] border border-line bg-glass p-3">
          <div className="flex items-center gap-2.5">
            <span aria-hidden="true" className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-accent-soft text-xs font-bold tracking-[0.02em] text-accent-2 shadow-[inset_0_0_0_1px_rgba(139,92,246,0.35)]">
              {initials}
            </span>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold">Operator</div>
              <div className="truncate text-[11px] text-muted">{email}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 border-t border-line-soft pt-2">
            <Button variant="ghost" size="sm" className="flex-1 justify-start px-2 text-muted" onClick={() => navigate("/settings")}>
              Zmień hasło
            </Button>
            <Button variant="ghost" size="sm" className="w-8 px-0 text-muted" aria-label="Wyloguj" title="Wyloguj" onClick={() => void logout()}>
              <LogOutIcon size={16} />
            </Button>
          </div>
        </div>
      </aside>

      <main className="mx-auto w-full min-w-0 max-w-[1100px] px-9 pb-16 pt-7">
        <PanelOutlet />
      </main>
    </div>
  );
}
